import ParameterValidator from '@mvps-genesis/parameter-validator';

/*
 *--------------------------------------------------------------------------
 * Read More
 *--------------------------------------------------------------------------
 *
 * This class handles read more / read less functionality.
 *
 */

export default class ReadMore {

	/**
	 * Parameter Validator class object
	 *
	 * @var	@mvps-genesis/parameter-validator	ParameterValidator
	 */
	#validator;

	/**
	 * The action element to trigger the read more / read less toggle
	 *
	 * @var	HTMLElement
	 */
	actionElement;

	/**
	 * The condensed content element text content when read more display mode is active
	 *
	 * @var	String
	 */
	condensedContent;

	/**
	 * The root container element
	 *
	 * @var	HTMLElement
	 */
	containerElement;

	/**
	 * The container element for the read more / read less content
	 *
	 * @var	HTMLElement
	 */
	contentElement;

	/**
	 * The show more content word limit for large window sizes
	 *
	 * @var	Number
	 */
	contentMaxWordsLarge;

	/**
	 * The show more content word limit for small window sizes
	 *
	 * @var	Number
	 */
	contentMaxWordsSmall;

	/**
	 * The list of words within the content element text content
	 *
	 * @var	Array
	 */
	contentWords;

	/**
	 * The number of words within the list of content element text content
	 *
	 * @var	Number
	 */
	contentWordCount;

	/**
	 * A MediaQueryList object used for detecting large width windows
	 *
	 * @var	MediaQueryList
	 */
	matchMedia;

	/**
	 * The current max words count based on the current window width
	 *
	 * @var	Number
	 */
	maxWordsCount;

	/**
	 * The original HTML content of the content element
	 *
	 * @var	String
	 */
	originalContent;

	/**
	 * The class identifier for the read less display mode
	 *
	 * @var	String
	 */
	readLessClass;

	/**
	 * Create a new ReadMore object instance
	 *
	 * @property	HTMLElement	actionElement			The action element to trigger the read more / read less toggle.
	 * @property	HTMLElement	containerElement		The root container element.
	 * @property	HTMLElement	contentElement			The container element for the read more / read less content.
	 * @property	Number		contentMaxWordsLarge	The show more content word limit for large window sizes.
	 * @property	Number		contentMaxWordsSmall	The show more content word limit for small window sizes.
	 * @property	String		windowQueryLarge		The large window width media query.
	 */
	constructor ({
		actionElement = '.js-read-more-toggle',
		containerElement = '.g-read-more',
		contentElement = '.g-read-more__content',
		contentMaxWordsLarge = 32,
		contentMaxWordsSmall = 24,
		windowQueryLarge = '(min-width: 960px)'
	} = {}) {
		this.#validator = new ParameterValidator(this);
		this.containerElement = this.#validator.isElement('element', containerElement);
		this.actionElement = this.#validator.isElement('ctaElement', actionElement, {container: this.containerElement});
		this.contentElement = this.#validator.isElement('innerElement', contentElement, {container: this.containerElement});
		this.matchMedia = window.matchMedia(this.#validator.isString('windowQueryLarge', windowQueryLarge));
		this.contentMaxWordsLarge = this.#validator.isInt('contentMaxWordsLarge', contentMaxWordsLarge);
		this.contentMaxWordsSmall = this.#validator.isInt('contentMaxWordsSmall', contentMaxWordsSmall);
		this.readLessClass = 'is-read-less';

		this.init();
	}

	init () {
		this.setContent();
		this.mediaChanged(this.matchMedia);
		this.bindEvents();
	}

	setContent () {
		this.originalContent = this.contentElement.innerHTML;
	}

	mediaChanged (event) {
		this.maxWordsCount = event.matches ? this.contentMaxWordsLarge : this.contentMaxWordsSmall;
		this.contentWords = this.originalContent.trim().split(/\s+/);
		this.contentWordCount = this.contentWords.length;

		if (this.contentWordCount > this.maxWordsCount) {
			this.activate();
		} else {
			this.deactivate();
		}
	}

	bindEvents () {
		this.matchMedia.addEventListener('change', (event) => this.mediaChanged(event));
		this.actionElement.addEventListener('click', this.toggleContent.bind(this));
	}

	activate () {
		if (this.actionElement.classList.contains(this.readLessClass)) {
			this.setCondensedContent(true);
		} else {
			this.setReadMoreContent(true);
		}

		this.actionElement.classList.remove('u-hidden');
	}

	deactivate () {
		this.resetReadMoreContent();
		this.actionElement.classList.add('u-hidden');
	}

	setCondensedContent (forceRefresh = false) {
		if (this.condensedContent && !forceRefresh) {
			return;
		}

		this.condensedContent = this.contentWords.slice(0, this.maxWordsCount)
			.join(' ')
			.replace(/\.+$/, '');
	}

	setReadMoreContent (forceRefresh = false) {
		this.setCondensedContent(forceRefresh);

		this.contentElement.innerHTML = `${this.condensedContent}... `;
	}

	toggleContent (event) {
		event.preventDefault();

		if (this.actionElement.classList.contains(this.readLessClass)) {
			this.showReadMore();
		} else {
			this.showReadLess();
		}
	}

	showReadMore () {
		this.actionElement.textContent = 'read more';

		this.actionElement.classList.remove(this.readLessClass);
		this.setReadMoreContent();
	}

	showReadLess () {
		this.actionElement.textContent = 'read less';

		this.actionElement.classList.add(this.readLessClass);
		this.resetReadMoreContent();
	}

	resetReadMoreContent () {
		this.contentElement.innerHTML = this.originalContent;
	}
}
